import React, { useState, useEffect, useRef } from "react";
import Main from "./Main";
import AddFund from "../components/AddFund";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArrowDropdown } from "react-icons/io";
import { MdArrowDropUp } from "react-icons/md";
import { getFundHistoryByUser } from "../api/fundTransferApi";
import { useNotify } from "../context/NotifyContext";
import WalletHistoryBar from "../components/WalletHistoryBar";

import { autoDateFormat } from "../utils/autoDateFormate";
import { exportTable } from "../utils/downloadExcel";
import {
  ListingFilters,
  ListingPagination,
  SortableTh,
  CopyText,
  AppBreadcrumbs,
  useListingState,
  FUND_FLOW_OPTIONS,
  StatusBadge,
} from "../components/listing";

const FundHistory = () => {
  const { notify } = useNotify();
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(null);
  const [members, setTeamMembers] = useState([]);
  const [membersDetails, setTeamDetails] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const listing = useListingState({ sortBy: "date", sortDir: "desc", includeDates: false });
  const [totalPages, setTotalPages] = useState(1);
  const [totalbyall, setTotalbyAll] = useState(0);
  const [totalDebitAmount, setTotalDebitAmount] = useState(0);
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const handleClick = (event, memberId) => {
    if (anchorEl && reportOpen === memberId) {
      setAnchorEl(null);
      setReportOpen(null);
      return;
    }
    setAnchorEl(event.currentTarget);
    setReportOpen(memberId);
  };

  const fetchTeams = async () => {
    if (!id) {
      notify("User not found", "error");
      return;
    }
    try {
      const result = await getFundHistoryByUser(id, {
        page: listing.page,
        limit: listing.limit,
        search: listing.debouncedSearch || undefined,
        sortBy: listing.sortBy || undefined,
        sortDir: listing.sortBy ? listing.sortDir : undefined,
        status: listing.status || undefined,
      });
      if (result?.success) {
        const response = result?.data;
        setTeamMembers(response?.funddata || []);
        setTeamDetails(response?.userDet || null);
        setTotalPages(response?.pagination?.totalPages || 1);
        setTotalbyAll(response?.pagination?.totalUsers || 0);
        setTotalCreditAmount(response?.totalCredit || 0);
        setTotalDebitAmount(response?.totalDebit || 0);
      } else {
        notify(result?.message || "Failed to fetch fund history", "error");
      }
    } catch (error) {
      console.error("Fetch team error:", error);
      notify("Something went wrong while fetching team members", "error");
    }
  };

  useEffect(() => {
    if (id) fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, listing.page, listing.limit, listing.debouncedSearch, listing.sortBy, listing.sortDir, listing.status]);

  const handleOpenFund = (member) => {
    setSelectedMember(member);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedMember(null);
    fetchTeams();
    setAnchorEl(null);
    setReportOpen(null);
  };

  return (
    <Main showRightPanel={false} showInfoText={false}>
      <div className="mt-4">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Teams", to: "/team" },
            { label: "History" },
          ]}
        />

        <WalletHistoryBar to="/main-wallet-ledger">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={`${members?.logo}`}
              alt="profile"
              className="h-10 w-8 sm:h-10 sm:w-10 object-cover rounded-ds shrink-0"
            />
            <div className="flex items-center gap-2 whitespace-nowrap min-w-0">
              <p className="text-[15px] font-semibold text-[var(--text-dark)]">
                {membersDetails?.name}
              </p>
              <p className="text-[18px] font-semibold text-[var(--text-muted)]">|</p>
              <p className="text-[15px] font-semibold text-[var(--text-muted)]">
                ID: {membersDetails?.userName}
              </p>
            </div>
          </div>
        </WalletHistoryBar>

        <div className="lg:hidden mt-3 flex flex-col gap-3">
          {members?.map((member, index) => (
            <div key={index} className="ds-card">
              <div className="flex flex-col gap-3 text-sm text-[var(--text-dark)]">
                <div>
                  <p className="font-semibold text-[var(--text-muted)]">Transiction Date:</p>
                  <p>{member?.createdAt ? autoDateFormat(member.createdAt, "full") : "N/A"}</p>
                </div>
                <div className="grid grid-cols-2">
                  <div className="flex flex-col items-start">
                    <p className="font-semibold text-[var(--text-muted)]">FromUser:</p>
                    <p>{member?.fromUser?.userName || "Guest"}</p>
                  </div>
                  <div className="flex flex-col justify-end items-end">
                    <p className="font-semibold text-[var(--text-muted)]">ToUser:</p>
                    <p>{member?.toUser?.userName || "Guest"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div>
                    <p className="font-semibold text-[var(--text-muted)]">TxtType:</p>
                    <StatusBadge status={member?.transactionType} />
                  </div>
                  <div className="flex flex-col justify-end items-end">
                    <p className="font-semibold text-[var(--text-muted)]">Status:</p>
                    <StatusBadge status={member?.transactionStatus} />
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div>
                    <p className="font-semibold text-[var(--text-muted)]">Amount:</p>
                    <p>{Number(member?.amount || 0).toFixed(2)}</p>
                  </div>
                  <div className="flex flex-col justify-end items-end">
                    <p className="font-semibold text-[var(--text-muted)]">TxtId:</p>
                    <CopyText value={member?.txnId} label="Txn ID" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => handleOpenFund(member)}
                  className="ds-action-btn"
                >
                  Add Fund
                </button>
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() =>
                      setReportOpen(reportOpen === member?._id ? null : member?._id)
                    }
                    className="ds-action-btn w-full"
                  >
                    Reports ▼
                  </button>
                  {reportOpen === member?._id && (
                    <div className="absolute left-0 mt-1 w-36 bg-[var(--surface-color)] border border-[var(--border-color)] rounded-md shadow-md text-xs z-50 make-text-primary">
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">Money Transfer</p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">Paytm AePS</p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">Fino AePS</p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">Aeps Server -2</p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">
                        RazorPay V1 Add Money
                      </p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">
                        RazorPay V3 Add Money
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalbyall}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
            limitOptions={[1, 10, 20, 50, 100]}
          />
        </div>

        <div className="ds-card mt-4 hidden lg:block">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex justify-start gap-3">
              <div className="ds-card !p-2 min-w-[150px]">
                <p className="text-[16px] text-[var(--text-dark)]">
                  {Number(totalCreditAmount || 0).toFixed(2)}
                </p>
                <p className="font-semibold text-[var(--text-muted)] text-[13px]">Total Credit</p>
              </div>
              <div className="ds-card !p-2 min-w-[150px]">
                <p className="text-[16px] text-[var(--text-dark)]">
                  {Number(totalDebitAmount || 0).toFixed(2)}
                </p>
                <p className="font-semibold text-[var(--text-muted)] text-[13px]">Total Debit</p>
              </div>
            </div>
          </div>

          <ListingFilters
            onClear={listing.clearFilters}
            showDates={false}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search"
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={FUND_FLOW_OPTIONS}
            statusLabel="Type"
            onExport={() =>
              exportTable(
                "fund-history",
                [
                  { label: "Transaction ID", key: "txnId" },
                  {
                    label: "Transfer Date",
                    value: (r) => (r?.createdAt ? autoDateFormat(r.createdAt, "full") : ""),
                  },
                  { label: "From", value: (r) => r?.fromUser?.userName ?? "" },
                  { label: "To", value: (r) => r?.toUser?.userName ?? "" },
                  { label: "Type", key: "transactionType" },
                  { label: "Status", key: "transactionStatus" },
                  { label: "Remark", key: "remarks" },
                  { label: "Amount", key: "amount" },
                ],
                members,
                notify
              )
            }
          />

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[800px]">
              <thead>
                <tr>
                  <th>TransictionID</th>
                  <SortableTh
                    label="Transfer Date"
                    sortKey="date"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>From</th>
                  <th>To</th>
                  <th>Type</th>
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Remark</th>
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member, i) => (
                  <tr key={member?._id || i}>
                    <td>
                      <CopyText value={member?.txnId} label="Txn ID" />
                    </td>
                    <td>
                      {member?.createdAt ? autoDateFormat(member.createdAt, "full") : "N/A"}
                    </td>
                    <td>{member?.fromUser?.userName || "-"}</td>
                    <td>{member?.toUser?.userName || "_"}</td>
                    <td>
                      <StatusBadge status={member?.transactionType} />
                    </td>
                    <td>
                      <StatusBadge status={member?.transactionStatus} />
                    </td>
                    <td className="uppercase">{member?.remarks || "fund"}</td>
                    <td className="uppercase">{Number(member?.amount || 0).toFixed(2)}</td>
                    <td>
                      <div className="relative" ref={dropdownRef}>
                        <Button
                          onClick={(e) => handleClick(e, member?._id)}
                          variant="text"
                          style={{ fontWeight: 300, fontSize: 12, border: ".1px solid gray" }}
                        >
                          Reports{" "}
                          {anchorEl && reportOpen === member._id ? (
                            <IoMdArrowDropdown />
                          ) : (
                            <MdArrowDropUp />
                          )}
                        </Button>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && reportOpen === member?._id}
                          onClose={handleClose}
                          PaperProps={{ style: { maxHeight: 200 } }}
                        >
                          <MenuItem onClick={() => navigate(`/order_details/${member?._id}`)}>
                            View
                          </MenuItem>
                          <MenuItem onClick={() => navigate(`/order_details/${member?._id}`)}>
                            Print Labels
                          </MenuItem>
                          <MenuItem onClick={() => navigate(`/order_details/${member?._id}`)}>
                            Print Receipt
                          </MenuItem>
                          <MenuItem onClick={() => navigate(`/order_details/${member?._id}`)}>
                            Settle Order
                          </MenuItem>
                        </Menu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalbyall}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
            limitOptions={[1, 10, 20, 50, 100]}
          />
        </div>
      </div>
      {open && <AddFund open={open} close={handleClose} memberData={selectedMember} />}
    </Main>
  );
};

export default FundHistory;
